import React, { useState, useEffect } from 'react';
import { archiveAPI } from '../services/api';
import { useApp } from '../context/AppContext';
import { decrypt, decryptData } from '../utils/security';

const GRADIENTS = [
  'linear-gradient(150deg,#D4A5A3,#C08A88)',
  'linear-gradient(150deg,#A3B4CC,#8898B8)',
  'linear-gradient(150deg,#A3C4B4,#88AE9E)',
  'linear-gradient(150deg,#C4B488,#AE9E74)',
  'linear-gradient(150deg,#B4A3C8,#9E88B4)',
  'linear-gradient(150deg,#C4A3B4,#AE8E9E)',
  'linear-gradient(150deg,#B4B4A8,#9E9E94)',
];

const ArchivePage = ({ onOpenBook }) => {
  const { getToken } = useApp();
  const [books, setBooks]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [page, setPage]         = useState(1);
  const [pagination, setPag]    = useState(null);
  const [search, setSearch]     = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const params = { page, limit: 20 };
        if (search) params.search = search;
        const res = await archiveAPI.getAll(token, params);
        // FIX: decrypt once here instead of on every render inside JSX.
        const decrypted = decryptData(res.data || []);
        setBooks(decrypted);
        setPag(res.pagination);
        if (!selected && decrypted.length > 0) setSelected(decrypted[0]);
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [page, search, getToken]);

  const handleSearch = (e) => {
    if (e.key === 'Enter') { setSearch(searchInput); setPage(1); }
  };

  return (
    <div style={{ background:'var(--sand-pale)', minHeight:'100vh', fontFamily:'Nunito,system-ui,sans-serif' }}>

      {/* Header */}
      <div className="px-4 py-3 border-b"
        style={{ background:'linear-gradient(135deg,#EDE0CE,#E8D9C0)', borderColor:'#D8C8A8' }}>
        <div className="font-display font-bold" style={{ fontSize:16, color:'var(--ink)', fontFamily:'Playfair Display,serif' }}>
          📚 Commentary Archive
        </div>
        <div className="uppercase" style={{ fontSize:9, color:'var(--ink-lt)', letterSpacing:1.5, fontFamily:'Nunito,sans-serif', marginTop:2 }}>
          Historic editions · Judicial & academic reference · {pagination?.total || 0} titles
        </div>
      </div>

      {/* Warning */}
      <div className="mx-4 mt-3 rounded-r-lg"
        style={{ background:'var(--amber-pale)', border:'1px solid #DEC088', borderLeft:'3px solid var(--amber-soft)', padding:'8px 11px', fontSize:11, color:'#7A5A30', lineHeight:1.5, fontFamily:'Nunito,sans-serif' }}>
        ⚠ Superseded editions — use only when researching the law as it stood at the time of a specific transaction or proceeding.
      </div>

      {/* Search */}
      <div className="px-4 pt-3">
        <input
          placeholder="Search archive titles…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={handleSearch}
          className="w-full rounded-lg px-3 py-2 border outline-none"
          style={{ fontSize:12, fontFamily:'Nunito,sans-serif', borderColor:'#DFD4BE', background:'rgba(255,255,255,.8)', color:'var(--ink)' }}/>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16 gap-3">
          <div className="rounded-full border-t-2 animate-spin"
            style={{ width:24, height:24, border:'2px solid #DED4C0', borderTopColor:'var(--sand)' }}/>
          <span style={{ fontSize:12, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif' }}>Loading archive…</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && books.length === 0 && (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'64px 24px', textAlign:'center' }}>
          <div style={{ fontSize:48, opacity:.3, marginBottom:16 }}>📦</div>
          <div style={{ fontFamily:'Playfair Display,serif', fontSize:20, fontWeight:700, color:'var(--ink)', marginBottom:8 }}>
            Archive Coming Soon
          </div>
          <div style={{ fontSize:13, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif', maxWidth:360, lineHeight:1.6 }}>
            AIR's historic commentary editions are being digitized and will be available here soon. Contact AIR editorial team for access to superseded editions.
          </div>
        </div>
      )}

      {!loading && books.length > 0 && (
        <div className="flex flex-col md:flex-row gap-4 p-4">

          {/* Left — Book list selector */}
          <div className="md:w-64 flex-shrink-0">
            <div className="font-bold uppercase mb-2"
              style={{ fontSize:9, color:'var(--ink-lt)', letterSpacing:2, fontFamily:'Nunito,sans-serif' }}>
              Select Title
            </div>
            <div className="flex flex-col gap-2" style={{ maxHeight:500, overflowY:'auto' }}>
              {books.map((book, i) => {
                const grad = GRADIENTS[book._id % GRADIENTS.length];
                return (
                  <button key={book._id} onClick={() => setSelected(book)}
                    className="flex items-center gap-3 p-3 rounded-xl border text-left transition-all w-full cursor-pointer"
                    style={{
                      background:  selected?._id === book._id ? 'rgba(255,255,255,.9)' : 'rgba(255,255,255,.5)',
                      borderColor: selected?._id === book._id ? 'var(--sage)' : '#DFD4BE',
                    }}>
                    <div className="rounded-md flex-shrink-0"
                      style={{ width:32, height:42, background:grad }}/>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate" style={{ fontSize:11, color:'var(--ink)', fontFamily:'Nunito,sans-serif', lineHeight:1.3 }}>
                        {decrypt(book.book_name || book.book_title)}
                      </div>
                      {book.category?.name && (
                        <div style={{ fontSize:9, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif', marginTop:2 }}>
                          {decrypt(book.category.name)}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <div className="flex justify-center gap-2 mt-3">
                <button disabled={!pagination.has_prev} onClick={() => setPage(p => p-1)}
                  className="px-3 py-1 rounded border disabled:opacity-40 cursor-pointer"
                  style={{ fontSize:11, borderColor:'#DFD4BE', color:'var(--sand)', fontFamily:'Nunito,sans-serif' }}>‹</button>
                <span style={{ fontSize:11, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif', padding:'4px 6px' }}>
                  {pagination.page}/{pagination.total_pages}
                </span>
                <button disabled={!pagination.has_next} onClick={() => setPage(p => p+1)}
                  className="px-3 py-1 rounded border disabled:opacity-40 cursor-pointer"
                  style={{ fontSize:11, borderColor:'#DFD4BE', color:'var(--sand)', fontFamily:'Nunito,sans-serif' }}>›</button>
              </div>
            )}
          </div>

          {/* Right — Edition detail for selected book */}
          {selected && (
            <div className="flex-1">
              <div className="font-bold uppercase mb-2"
                style={{ fontSize:9, color:'var(--ink-lt)', letterSpacing:2, fontFamily:'Nunito,sans-serif' }}>
                {decrypt(selected.book_name || selected.book_title)}
              </div>

              {/* Book info card */}
              <div className="bg-white rounded-xl border p-4 mb-3"
                style={{ borderColor:'var(--sage)' }}>
                <div className="flex items-start gap-3">
                  <div className="rounded-md flex-shrink-0"
                    style={{ width:48, height:64, background:GRADIENTS[selected._id % GRADIENTS.length] }}/>
                  <div className="flex-1">
                    <div className="font-display font-bold mb-1"
                      style={{ fontFamily:'Playfair Display,serif', fontSize:15, color:'var(--ink)' }}>
                      {decrypt(selected.book_name || selected.book_title)}
                    </div>
                    {selected.authors?.length > 0 && (
                      <div style={{ fontSize:11, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif', marginBottom:4 }}>
                        {selected.authors.map(a => decrypt(a.name)).join(', ')}
                      </div>
                    )}
                    {selected.category?.name && (
                      <div style={{ fontSize:10, color:'var(--ink-faint)', fontFamily:'Nunito,sans-serif' }}>
                        {decrypt(selected.category.name)}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className="rounded font-bold"
                        style={{ fontSize:8, padding:'2px 7px', background:'var(--amber-pale)', color:'#7A5A30', fontFamily:'Nunito,sans-serif' }}>
                        {selected.state?.toUpperCase() || 'ARCHIVE'}
                      </span>
                    </div>
                  </div>
                </div>

                {selected.book_short_description && (
                  <div className="mt-3 pt-3 border-t"
                    style={{ borderColor:'var(--border-lt)', fontSize:11, color:'var(--ink-mid)', lineHeight:1.6, fontFamily:'Nunito,sans-serif' }}
                    dangerouslySetInnerHTML={{ __html: decrypt(selected.book_short_description).replace(/<[^>]*>/g,'').substring(0, 250) + '…' }}/>
                )}

                <button className="w-full mt-3 py-2 text-white rounded-lg font-bold border-0 cursor-pointer hover:opacity-90 transition-opacity"
                  style={{ background:'var(--rose)', fontSize:12, fontFamily:'Nunito,sans-serif' }}
                  onClick={() => onOpenBook && onOpenBook(selected)}>
                  Open Edition →
                </button>
              </div>

              {/* Archive notice */}
              <div className="rounded-xl border p-4"
                style={{ background:'rgba(255,255,255,.6)', borderColor:'#DFD4BE' }}>
                <div className="font-bold uppercase mb-2"
                  style={{ fontSize:9, color:'var(--sand)', letterSpacing:2, fontFamily:'Nunito,sans-serif' }}>
                  Archive Notice
                </div>
                <div style={{ fontSize:11, color:'var(--ink-mid)', lineHeight:1.6, fontFamily:'Nunito,sans-serif' }}>
                  Previous editions of this title are preserved in AIR's physical and digital archive. Contact AIR editorial team for access to superseded editions for historical legal research.
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <div className="rounded font-bold"
                    style={{ fontSize:8, padding:'2px 7px', background:'#EDE0CE', color:'#7A5A30', fontFamily:'Nunito,sans-serif' }}>
                    SUPERSEDED EDITIONS
                  </div>
                  <span style={{ fontSize:10, color:'var(--ink-lt)', fontFamily:'Nunito,sans-serif' }}>
                    Available on request
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ArchivePage;